<template>
  <ReservationPanel sticky @search="getAccommodationData" />

  <div class="container--Medium">
    <div class="accommodationList__Intro">
      <h1 v-text="page.title" />
      <client-only>
        <h2 v-text="pageTitle" />
      </client-only>
      <div>
        <CMSField v-if="page?.content" :data="page.content" />
      </div>
    </div>

    <div class="accommodationList__FiltersWrapper">
      <Filters :data="filtersList" v-model="selectedFilters" @close="getFilteredData" @item-remove="updateResults" @route-change="updateResults" />
      <div v-if="!responseError" class="accommodationList__FiltersTop">
        <p>{{ $t('propertiesAvailable', accommodationsListing.length + unavailableAccommodationsListing.length) }}</p>
      </div>
    </div>

    <div v-if="responseError" class="apiFetchError">
      <p v-text="responseError" />
    </div>
    <template v-else>
      <div v-if="accommodationsListing.length" class="accommodationList__ListWrapper">
        <PropertyItem v-for="item in accommodationsListing" :property="item" :key="item.id" :short-name="true" />
      </div>
  
      <div v-if="unavailableAccommodationsListing.length" class="accommodationList__ListWrapper">
        <h2 v-text="$t('notAvailable')" />
        <PropertyItem v-for="item in unavailableAccommodationsListing" :property="item" not-available :key="item.id" :short-name="true" />
      </div>
    </template>
  </div>

  <Loader :active="fetchingData" />
</template>

<script setup>
  import { format } from 'date-fns';
  import { FilterIdentifiersObject } from '~/utils/types';
  const { t } = useI18n();
  const { booking } = useBooking();
  const { page } = usePage();
  const { initialData, accommodationsData } = useSetup();
  const selectedFilters = ref({});
  const accommodationsListing = ref([]);
  const unavailableAccommodationsListing = ref([]);
  const originalListing = ref([]);
  const originalUnavailableListing = ref([]);
  const fetchingData = ref(false);
  const responseError = ref(null);

  const props = defineProps({
    page: {},
    place: {
      type: String || Number,
    },
    kind: {
      type: String,
    }
  })

  const pageTitle = computed(() => props.page.subtitle);

  async function getAccommodationData() {
    fetchingData.value = true;
    responseError.value = null;
    let requestBody = { ...booking.value };
    if (requestBody.date_from) requestBody.date_from = format(new Date(requestBody.date_from), 'yyyy-MM-dd');
    if (requestBody.date_to) requestBody.date_to = format(new Date(requestBody.date_to), 'yyyy-MM-dd');
    if (props.page?.place_detail?.id) requestBody.place = props.page.place_detail.id;

    if (requestBody?.children_age?.length) {
      requestBody.children_age = requestBody.children_age.filter(item => item !== null);
      requestBody.children = requestBody.children_age.length;
      booking.value.children_age = JSON.parse(JSON.stringify(requestBody.children_age))
      booking.value.children = requestBody.children;
    }

    if (page.value?.parent_accommodation_kind_group) requestBody.accommodation_group = page.value.parent_accommodation_kind_group;
    else if (page.value?.accommodation_group?.id) requestBody.accommodation_group = page.value.accommodation_group.id;
    else delete requestBody.accommodation_group;

    try {
      const response = await useServerFetch('search/accommodation/', {
        method: 'POST',
        body: requestBody
      })
  
      if (response?.accommodations) {
        // save original data in originalListing
        originalListing.value = response.accommodations.map(item => ({ ...accommodationsData.value[item.accommodation], priceForPeriod: item.price || null }));
        accommodationsListing.value = Array.from(originalListing.value);
      }

      if (response?.unavailable_accommodations) {
        // save original data in originalUnavailableListing
        originalUnavailableListing.value = response.unavailable_accommodations.map(item => accommodationsData.value[item.accommodation]);
        unavailableAccommodationsListing.value = Array.from(originalUnavailableListing.value);
        
        let categoriesFilter = filtersList.value.find(item => item.identifier === FilterIdentifiersObject.CATEGORY_RATING.backendId);
        if (categoriesFilter) categoriesFilter.items = ratingFilters.value;
      }
    } catch(e) {
      console.error(e);
      if (e.data?.details?.code === 'no_results') {
        responseError.value = t('fetchErrors.accommodationsNoResults');
      }
    }
    fetchingData.value = false;
  }

  // collect all rating from accommodations and set them in filters so we can get stars ratings
  const ratingFilters = computed(() => {
    let allObjects = [...accommodationsListing.value, ...unavailableAccommodationsListing.value];
    return allObjects.reduce((acc, nextItem) => {
      if (nextItem.category_rating === 0) return acc;
      const ratingExistsInAcc = acc.find(item => item.id === nextItem.category_rating)
      if (!ratingExistsInAcc) acc.push({id: nextItem.category_rating, name: `${nextItem.category_rating}`})
      return acc;
    }, []).sort((a, b) => a.id - b.id);
  });

  const filtersList = ref([
    {
      title: t('filtersTexts.propertyType'),
      identifier: FilterIdentifiersObject.PROPERTY_TYPE.backendId,
      items: getPropertyTypeFilters(t)
    },
    {
      title: t('filtersTexts.mealPlans'),
      identifier: FilterIdentifiersObject.MEAL_PLAN.backendId,
      items: initialData.value.amenities?.[FilterIdentifiersObject.MEAL_PLAN.frontendId] ? initialData.value.amenities[FilterIdentifiersObject.MEAL_PLAN.frontendId] : []
    },
    {
      title: t('filtersTexts.parking'),
      identifier: FilterIdentifiersObject.PARKING.backendId,
      items: initialData.value.amenities?.[FilterIdentifiersObject.PARKING.frontendId] ? initialData.value.amenities[FilterIdentifiersObject.PARKING.frontendId] : []
    },
    {
      title: t('filtersTexts.categoryRating'),
      identifier: FilterIdentifiersObject.CATEGORY_RATING.backendId,
      items: ratingFilters.value
    },
    {
      title: t('filtersTexts.holidayType'),
      identifier: FilterIdentifiersObject.HOLIDAY_TYPE.backendId,
      items: initialData.value.amenities?.[FilterIdentifiersObject.HOLIDAY_TYPE.frontendId] ? initialData.value.amenities[FilterIdentifiersObject.HOLIDAY_TYPE.frontendId] : []
    },
    {
      title: t('filtersTexts.brand'),
      identifier: FilterIdentifiersObject.BRAND.backendId,
      items: initialData.value.brands
    },
  ]);

  if (props.kind) filtersList.value = filtersList.value.slice(1);

  function getFilteredData() {
    if (originalListing.value?.length) accommodationsListing.value = filterData(originalListing.value);
    if (originalUnavailableListing.value?.length) unavailableAccommodationsListing.value = filterData(originalUnavailableListing.value);
  }

  function filterData(data) {
    let filteredResults = JSON.parse(JSON.stringify(data));

    // get all amenities identifiers from types.ts
    const amenitiesIdentificators = [FilterIdentifiersObject.PARKING.backendId, FilterIdentifiersObject.MEAL_PLAN.backendId, FilterIdentifiersObject.HOLIDAY_TYPE.backendId];

    // set filters listing - backendId to frontendId
    const FILTERS_MAPPER = {
      [FilterIdentifiersObject.PROPERTY_TYPE.backendId]: FilterIdentifiersObject.PROPERTY_TYPE.frontendId,
      [FilterIdentifiersObject.MEAL_PLAN.backendId]: FilterIdentifiersObject.MEAL_PLAN.frontendId,
      [FilterIdentifiersObject.PARKING.backendId]: FilterIdentifiersObject.PARKING.frontendId,
      [FilterIdentifiersObject.CATEGORY_RATING.backendId]: FilterIdentifiersObject.CATEGORY_RATING.frontendId,
      [FilterIdentifiersObject.HOLIDAY_TYPE.backendId]: FilterIdentifiersObject.HOLIDAY_TYPE.frontendId,
      [FilterIdentifiersObject.BRAND.backendId]: FilterIdentifiersObject.BRAND.frontendId,
    }

    const filtersWithValues = JSON.parse(JSON.stringify(selectedFilters.value));
    for (let filter in filtersWithValues) {
      if (!filtersWithValues[filter].length) {
        delete filtersWithValues[filter];
      }
    }
    
    for (let accommodation of filteredResults) {
      for (let filter in filtersWithValues) {
        const accommodationIncludesFilter = filtersWithValues[filter].some(selectedFilter => {
          if (amenitiesIdentificators.includes(filter)) {
            return Array.isArray(accommodation.amenities[FILTERS_MAPPER[filter]]) ? accommodation.amenities[FILTERS_MAPPER[filter]].includes(selectedFilter) : accommodation.amenities[FILTERS_MAPPER[filter]] === selectedFilter;
          } else {
            return Array.isArray(accommodation[FILTERS_MAPPER[filter]]) ? accommodation[FILTERS_MAPPER[filter]].includes(selectedFilter) : accommodation[FILTERS_MAPPER[filter]] === selectedFilter;
          }
        })

        if (!accommodationIncludesFilter) {
          filteredResults = filteredResults.filter(item => item !== accommodation);
        }
      }
    }

    return filteredResults;
  }

  function updateResults() {
    setTimeout(() => {
      getFilteredData();
    }, 0);
  }

  onMounted(() => {
    setTimeout(async () => {
      await getAccommodationData();
      if (selectedFilters.value) getFilteredData();
    }, 10);
  })
</script>

<style lang="scss">
  @import url('@/assets/styles/pages/_accommodationList.scss');
</style>